import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cursosAPI, pagosAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  GraduationCap, Calendar, Clock, MapPin, Users, DollarSign, 
  Target, BookOpen, Award, UserCheck, ChevronDown, ChevronUp, Loader2, CreditCard
} from 'lucide-react';
import MercadoPagoCheckout from '../../components/MercadoPagoCheckout';

const CapacitacionesPage = () => {
  const [searchParams] = useSearchParams();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCurso, setExpandedCurso] = useState(null);
  const [showInscripcion, setShowInscripcion] = useState(false);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [inscripcionData, setInscripcionData] = useState({
    nombre_completo: '',
    dni: '',
    email: '',
    telefono: '',
    curso_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [datosValidados, setDatosValidados] = useState(false);

  useEffect(() => {
    loadCursos();
  }, []);

  // Abrir automáticamente el curso si viene en la URL
  useEffect(() => {
    const cursoId = searchParams.get('curso');
    if (cursoId && cursos.length > 0) {
      setExpandedCurso(cursoId);
      // Hacer scroll al curso
      setTimeout(() => {
        const element = document.getElementById(`curso-${cursoId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [searchParams, cursos]);

  const loadCursos = async () => {
    try {
      const response = await cursosAPI.getAll({ estado: 'publicado' });
      setCursos(response.data);
    } catch (error) {
      console.error('Error loading cursos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Por definir';
    // Parsear solo la parte de fecha (YYYY-MM-DD) para evitar problemas de timezone
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    // Crear fecha usando componentes locales (no UTC)
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const openInscripcion = (curso) => {
    setSelectedCurso(curso);
    setInscripcionData(prev => ({ ...prev, curso_id: curso.id }));
    setShowInscripcion(true);
    setShowPayment(false);
    setDatosValidados(false);
  };

  // Validar datos antes de mostrar pago
  const handleValidarDatos = (e) => {
    e.preventDefault();
    
    if (!inscripcionData.nombre_completo || !inscripcionData.dni || !inscripcionData.email) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }

    // Si el curso tiene precio en USD, mostrar paso de pago
    const precio = selectedCurso?.precio_usd || 0;
    if (precio > 0) {
      setDatosValidados(true);
      setShowPayment(true);
    } else {
      // Curso gratuito - inscribir directamente
      handleInscripcionGratuita();
    }
  };

  // Inscripción para cursos gratuitos
  const handleInscripcionGratuita = async () => {
    setSubmitting(true);
    try {
      await cursosAPI.inscripcionPublica(inscripcionData);
      toast.success('¡Inscripción realizada correctamente!');
      setShowInscripcion(false);
      setInscripcionData({ nombre_completo: '', dni: '', email: '', telefono: '', curso_id: '' });
      setShowPayment(false);
      setDatosValidados(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al procesar la inscripción';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInscripcion = async (e) => {
    e.preventDefault();
    
    if (!inscripcionData.nombre_completo || !inscripcionData.dni || !inscripcionData.email) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      const response = await cursosAPI.inscripcionPublica(inscripcionData);
      toast.success('¡Inscripción realizada correctamente!');
      setShowInscripcion(false);
      setInscripcionData({ nombre_completo: '', dni: '', email: '', telefono: '', curso_id: '' });
      // Aquí se podría redirigir a MercadoPago cuando esté implementado
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al procesar la inscripción';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="bg-white/10 rounded-full p-4 w-fit mx-auto mb-4">
            <GraduationCap className="h-12 w-12" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Formación profesional para personal aeronáutico
          </h1>
          <p className="text-orange-100 text-lg max-w-2xl mx-auto">
            Capacitaciones certificadas para mecánicos, técnicos, pilotos y todo personal de la industria aeronáutica
          </p>
        </div>
        {/* Gradient transition naranja → fondo claro, suaviza el borde inferior */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none" />
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-slate-500">Cargando capacitaciones...</p>
          </div>
        ) : cursos.length > 0 ? (
          <div className="space-y-6">
            {cursos.map(curso => (
              <Card 
                key={curso.id}
                id={`curso-${curso.id}`}
                className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="h-2 bg-gradient-to-r from-orange-400 to-orange-500" />
                
                <CardHeader className="pb-2">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-3 rounded-xl flex-shrink-0">
                        <GraduationCap className="h-8 w-8 text-orange-500" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-slate-800">
                          {curso.nombre}
                        </CardTitle>
                        {curso.modalidad && (
                          <Badge className="mt-2 bg-orange-100 text-orange-700">
                            {curso.modalidad}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {curso.aranceles && (
                        <p className="text-sm text-slate-600">{curso.aranceles}</p>
                      )}
                      {curso.precio_usd > 0 && (
                        <Badge variant="outline" className="text-lg px-3 py-1">
                          USD ${curso.precio_usd}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Info básica */}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 py-4 border-y">
                    {curso.fecha_inicio && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-slate-400">Fecha de Inicio</p>
                          <p className="text-sm font-medium">{formatDate(curso.fecha_inicio)}</p>
                        </div>
                      </div>
                    )}
                    {curso.fecha_limite_inscripcion && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-slate-400">Inscripción hasta</p>
                          <p className="text-sm font-medium">{formatDate(curso.fecha_limite_inscripcion)}</p>
                        </div>
                      </div>
                    )}
                    {curso.duracion && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-slate-400">Duración</p>
                          <p className="text-sm font-medium">{curso.duracion}</p>
                        </div>
                      </div>
                    )}
                    {curso.certificacion && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Award className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-slate-400">Certificación</p>
                          <p className="text-sm font-medium">{curso.certificacion}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Descripción corta */}
                  {curso.descripcion && (
                    <p className="text-slate-600">{curso.descripcion}</p>
                  )}

                  {/* Sección expandible */}
                  {expandedCurso === curso.id && (
                    <div className="space-y-6 pt-4 border-t animate-in slide-in-from-top-2">
                      {/* Objetivos */}
                      {curso.objetivos && (
                        <div>
                          <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                            <Target className="h-5 w-5 text-orange-500" />
                            Objetivos
                          </h4>
                          <p className="text-slate-600 whitespace-pre-line pl-7">{curso.objetivos}</p>
                        </div>
                      )}

                      {/* Contenido */}
                      {curso.contenido && (
                        <div>
                          <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                            <BookOpen className="h-5 w-5 text-orange-500" />
                            Contenido
                          </h4>
                          <p className="text-slate-600 whitespace-pre-line pl-7">{curso.contenido}</p>
                        </div>
                      )}

                      {/* Docentes */}
                      {curso.docentes && (
                        <div>
                          <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                            <UserCheck className="h-5 w-5 text-orange-500" />
                            Docentes
                          </h4>
                          <div className="pl-7 space-y-2">
                            {curso.docentes.titular && (
                              <p className="text-slate-600">
                                <span className="font-medium">Titular:</span> {curso.docentes.titular.nombre}
                                {curso.docentes.titular.matricula && ` - Mat. ${curso.docentes.titular.matricula}`}
                              </p>
                            )}
                            {curso.docentes.suplente && (
                              <p className="text-slate-600">
                                <span className="font-medium">Suplente:</span> {curso.docentes.suplente.nombre}
                                {curso.docentes.suplente.matricula && ` - Mat. ${curso.docentes.suplente.matricula}`}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Requisitos */}
                      {curso.requisitos && (
                        <div>
                          <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                            <Users className="h-5 w-5 text-orange-500" />
                            Requisitos
                          </h4>
                          <p className="text-slate-600 whitespace-pre-line pl-7">{curso.requisitos}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botones */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setExpandedCurso(expandedCurso === curso.id ? null : curso.id)}
                      className="flex-1"
                    >
                      {expandedCurso === curso.id ? (
                        <>Ver menos <ChevronUp className="ml-2 h-4 w-4" /></>
                      ) : (
                        <>Ver más información <ChevronDown className="ml-2 h-4 w-4" /></>
                      )}
                    </Button>
                    <Button 
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      onClick={() => openInscripcion(curso)}
                    >
                      Inscribirme
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-orange-50 rounded-full p-6 w-fit mx-auto mb-4">
              <GraduationCap className="h-16 w-16 text-orange-300" />
            </div>
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              No hay capacitaciones disponibles
            </h2>
            <p className="text-slate-500 max-w-md mx-auto">
              Actualmente no hay capacitaciones programadas. Vuelve a consultar pronto o 
              contáctanos para conocer las próximas fechas.
            </p>
            <Button className="mt-6 bg-orange-500 hover:bg-orange-600">
              Contactar
            </Button>
          </div>
        )}
      </div>

      {/* Modal de Inscripción */}
      <Dialog open={showInscripcion} onOpenChange={(open) => {
        setShowInscripcion(open);
        if (!open) {
          setShowPayment(false);
          setDatosValidados(false);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inscripción a Capacitación</DialogTitle>
            <DialogDescription>
              {selectedCurso?.nombre}
            </DialogDescription>
          </DialogHeader>

          {!showPayment ? (
            // Paso 1: Formulario de datos
            <form onSubmit={handleValidarDatos} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre Completo *</Label>
                <Input
                  id="nombre"
                  value={inscripcionData.nombre_completo}
                  onChange={(e) => setInscripcionData(prev => ({ ...prev, nombre_completo: e.target.value }))}
                  placeholder="Juan Pérez"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dni">DNI *</Label>
                <Input
                  id="dni"
                  value={inscripcionData.dni}
                  onChange={(e) => setInscripcionData(prev => ({ ...prev, dni: e.target.value }))}
                  placeholder="12345678"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={inscripcionData.email}
                  onChange={(e) => setInscripcionData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="juan@email.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono (opcional)</Label>
                <Input
                  id="telefono"
                  value={inscripcionData.telefono || ''}
                  onChange={(e) => setInscripcionData(prev => ({ ...prev, telefono: e.target.value }))}
                  placeholder="+54 11 1234-5678"
                />
              </div>

              {selectedCurso?.precio_usd > 0 && (
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Arancel:</span> USD ${selectedCurso.precio_usd}
                  </p>
                  {selectedCurso?.aranceles && (
                    <p className="text-sm text-slate-500 mt-1">{selectedCurso.aranceles}</p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowInscripcion(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={submitting}
                >
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Procesando...</>
                  ) : selectedCurso?.precio_usd > 0 ? (
                    <>Continuar al Pago <CreditCard className="ml-2 h-4 w-4" /></>
                  ) : (
                    'Confirmar Inscripción'
                  )}
                </Button>
              </div>
            </form>
          ) : (
            // Paso 2: Pago con MercadoPago
            <div className="space-y-4">
              {/* Resumen de datos */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-2">Datos de inscripción</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p><span className="text-slate-400">Nombre:</span> {inscripcionData.nombre_completo}</p>
                  <p><span className="text-slate-400">DNI:</span> {inscripcionData.dni}</p>
                  <p><span className="text-slate-400">Email:</span> {inscripcionData.email}</p>
                </div>
              </div>

              {/* Checkout MercadoPago */}
              <MercadoPagoCheckout
                tipo="curso"
                titulo={`Inscripción: ${selectedCurso?.nombre?.substring(0, 30)}...`}
                descripcion="Pago seguro con MercadoPago"
                monto={selectedCurso?.precio_usd || 0}
                moneda="USD"
                createPreference={async () => {
                  const response = await pagosAPI.crearPreferenciaCurso({
                    curso_id: selectedCurso.id,
                    email: inscripcionData.email,
                    nombre: inscripcionData.nombre_completo,
                    telefono: inscripcionData.telefono
                  });
                  return response.data;
                }}
                buttonText="Pagar Inscripción"
                onError={(error) => toast.error(error)}
              />

              {/* Botón volver */}
              <Button 
                variant="outline" 
                onClick={() => setShowPayment(false)}
                className="w-full"
              >
                ← Modificar datos
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CapacitacionesPage;
